import type {
  AreaTag,
  AreaTagLayerProps,
  LayoutMarker,
  LayoutMarkerLayerProps,
} from '@ironview/interactive-viewer';

import type { PhotoMarker, Unit } from '../types/types';
import type { HeatMapIssueRange, HeatMapSummary } from '../utils/heatMap';
import { Ironview360AreaTagLabel, type PendingAreaTagTextChanges } from './Ironview360AreaTagLabel';
import {
  getIronview360AreaTagPresentation,
  getIronview360MarkerPresentation,
  isIronview360MarkerVisible,
} from './ironview360ScenePresentation';

type AreaTagCoreProps = Pick<
  AreaTagLayerProps,
  'areaTags' | 'getPoints' | 'getPresentation' | 'renderLabel'
>;

export interface Ironview360AreaTagLayerOptions {
  areaTags: readonly AreaTag[];
  sourceAreaTagsById: ReadonlyMap<string, Unit>;
  pendingPolygonChanges: ReadonlyMap<number, readonly (readonly number[])[]>;
  pendingTextChanges: ReadonlyMap<number, PendingAreaTagTextChanges>;
  areaHeatMapSummaries: ReadonlyMap<number, HeatMapSummary>;
  heatMapIssueFilter: HeatMapIssueRange;
  isHeatMapEnabled: boolean;
  showTooltips?: boolean;
  selectedAreaId?: number;
  hoveredAreaId?: number;
  fixedLabelFontSize?: number;
  interactive?: boolean;
  highlightedAreaIds?: ReadonlySet<number>;
}

export const createIronview360AreaTagLayerProps = ({
  areaTags,
  sourceAreaTagsById,
  pendingPolygonChanges,
  pendingTextChanges,
  areaHeatMapSummaries,
  heatMapIssueFilter,
  isHeatMapEnabled,
  showTooltips = true,
  selectedAreaId,
  hoveredAreaId,
  fixedLabelFontSize,
  interactive = true,
  highlightedAreaIds,
}: Ironview360AreaTagLayerOptions): AreaTagCoreProps => ({
  areaTags,
  getPoints: (areaTag) => pendingPolygonChanges.get(Number(areaTag.id)) ?? areaTag.points,
  getPresentation: (areaTag) => {
    const unit = sourceAreaTagsById.get(String(areaTag.id));
    if (!unit) return { visible: false };
    const basePresentation = getIronview360AreaTagPresentation({
      summary: areaHeatMapSummaries.get(unit.id),
      enabled: isHeatMapEnabled,
      showTooltips,
      filter: heatMapIssueFilter,
      selected: selectedAreaId === unit.id,
      hovered: hoveredAreaId === unit.id,
    });
    const presentation = highlightedAreaIds?.has(unit.id)
      ? {
          ...basePresentation,
          baseFill: '#4477ff',
          baseFillOpacity: 0.16,
          fill: '#4477ff',
          fillOpacity: 0.38,
          stroke: '#4477ff',
          strokeOpacity: 1,
          strokeWidth: 3,
        }
      : basePresentation;
    return interactive
      ? presentation
      : { ...presentation, pointerEvents: 'none', cursor: 'crosshair' };
  },
  renderLabel: (areaTag, context) => (
    <Ironview360AreaTagLabel
      areaTag={areaTag}
      context={context}
      pendingChanges={pendingTextChanges.get(Number(areaTag.id))}
      fixedFontSize={fixedLabelFontSize}
    />
  ),
});

type MarkerCoreProps = Pick<
  LayoutMarkerLayerProps,
  'markers' | 'editing' | 'isSelected' | 'getPresentation'
>;

export interface Ironview360MarkerLayerOptions {
  markers: readonly LayoutMarker[];
  sourcePhotoMarkersById: ReadonlyMap<string, PhotoMarker>;
  selectedMarkerIds: ReadonlySet<number>;
  markerHeatMapSummaries: ReadonlyMap<number, HeatMapSummary>;
  heatMapIssueFilter: HeatMapIssueRange;
  isHeatMapEnabled: boolean;
  showTooltips?: boolean;
  photoIndexMode: boolean;
  disableMarkerClicks?: boolean;
  selectedAreaId?: number;
  areaMarkerMap: ReadonlyMap<number, readonly number[]>;
  areaPhotoMap: ReadonlyMap<number, readonly number[]>;
  isIncludedInAnyArea: (sourceMarker: PhotoMarker, viewerMarker: LayoutMarker) => boolean;
}

export const createIronview360MarkerLayerProps = ({
  markers,
  sourcePhotoMarkersById,
  selectedMarkerIds,
  markerHeatMapSummaries,
  heatMapIssueFilter,
  isHeatMapEnabled,
  showTooltips = true,
  photoIndexMode,
  disableMarkerClicks = false,
  selectedAreaId,
  areaMarkerMap,
  areaPhotoMap,
  isIncludedInAnyArea,
}: Ironview360MarkerLayerOptions): MarkerCoreProps => ({
  markers,
  editing: photoIndexMode,
  isSelected: (viewerMarker) => {
    const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
    return marker ? selectedMarkerIds.has(marker.id) : false;
  },
  getPresentation: (viewerMarker) => {
    const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
    if (!marker) return { visible: false };
    const photoIds = viewerMarker.photos
      .map((photo) => Number(photo.id))
      .filter((id) => Number.isFinite(id));
    const isVisible = isIronview360MarkerVisible({
      selectedAreaId,
      markerPersistenceId: marker.dbId,
      photoIds,
      selectedAreaMarkerIds:
        selectedAreaId === undefined ? undefined : areaMarkerMap.get(selectedAreaId),
      selectedAreaPhotoIds:
        selectedAreaId === undefined ? undefined : areaPhotoMap.get(selectedAreaId),
      includedInAnyArea: isIncludedInAnyArea(marker, viewerMarker),
    });

    return getIronview360MarkerPresentation({
      summary: markerHeatMapSummaries.get(marker.id),
      enabled: isHeatMapEnabled,
      showTooltips,
      filter: heatMapIssueFilter,
      visible: isVisible,
      editing: photoIndexMode,
      disabled: disableMarkerClicks,
      finished: viewerMarker.photos.some((photo) => photo.scanType === 'Finished'),
      hasPhotos: viewerMarker.photos.length > 0,
    });
  },
});
